import { el, mount, unmount } from 'redom';

import application from 'application';
import serverInterface from 'server-interface';

import EventRegistration from 'lib/event-registration';
import OutsideClickEventRegistration from 'lib/outside-click-event-registration';

import internalEventCodes from 'event-system/codes/internal';

import SearchBar from './search-bar';
import SearchResultsList from './search-results-list';
import RecentSearchesDisplay from './recent-searches-display';

import { groupRecentSearchesByTime } from './utilities';
import { getSearchStrategyForPath } from './strategies';

import './index.scss';

class GenericSearchPanel {

  constructor() {

    this.searchStrategy = null;
    this.onClose = null;
    this.outsideClickEventRegistration = null;
    this.escapeKeyEventRegistration = null;
    this.isSearching = false;

    this.searchBar = new SearchBar();
    this.searchBar.setOnChange(this.handleSearchChange.bind(this));

    this.recentSearchesContainer = el('.recent-searches-container.hide-scrollbar');
    this.searchResultsContainer = el('.search-results-container.hide-scrollbar');
    this.emptyStateNoSearches = el('.search-empty-state', 'No searches yet');
    this.emptyStateNoResults = el('.search-empty-state', 'No results found');

    this.contentContainer = el('.search-panel-content', [
      this.recentSearchesContainer,
      this.searchResultsContainer,
      this.emptyStateNoSearches,
      this.emptyStateNoResults
    ]);

    this.el = el('.generic-search-panel', [
      this.searchBar.el,
      this.contentContainer
    ]);

  }

  show(configuration) {

    this.onClose = configuration.onClose;
    this.searchStrategy = getSearchStrategyForPath(window.location.pathname);

    this.searchBar.setValue(configuration.searchTerm);

    // Registered before the no-strategy return: a panel opened on a screen it cannot search still
    // has to close.
    this.registerCloseHandlers();

    if (this.searchStrategy === null) {
      this.showEmptyStateNoSearches();
      return;
    }

    this.handleSearchChange(configuration.searchTerm);

    this.searchBar.focus();

  }

  cancelCloseHandlers() {

    if (this.outsideClickEventRegistration === null) {
      return;
    }

    this.outsideClickEventRegistration.cancel();
    this.escapeKeyEventRegistration.cancel();
    this.outsideClickEventRegistration = null;
    this.escapeKeyEventRegistration = null;

  }

  hide() {

    this.cancelCloseHandlers();

    this.searchBar.cleanup();
    this.searchBar.hideSpinner();

  }

  requestClose() {

    if (this.onClose !== null) {
      this.onClose();
    }

  }

  handleKeyDown(event) {

    if (event.key !== 'Escape') {
      return;
    }

    this.requestClose();

  }

  // show() runs again whenever the address changes while the panel is open, so the previous
  // registrations are dropped rather than stacked.
  registerCloseHandlers() {

    this.cancelCloseHandlers();

    this.outsideClickEventRegistration = new OutsideClickEventRegistration(this.el, this.requestClose.bind(this));
    this.escapeKeyEventRegistration = new EventRegistration(document, 'keydown', this.handleKeyDown.bind(this));

  }

  handleSearchChange(searchTerm) {

    if (searchTerm.trim() === '') {
      this.loadRecentSearches();
      return;
    }

    this.performSearch(searchTerm);

  }

  async performSearch(searchTerm) {

    try {

      this.showLoadingState();
      this.searchBar.showSpinner();

      const results = await this.searchStrategy.search(searchTerm);

      this.searchBar.hideSpinner();
      this.displaySearchResults(results);

    } catch (exception) {
      console.error('Search failed:', exception);
      this.searchBar.hideSpinner();
      this.showEmptyStateNoResults();
    }

  }

  async loadRecentSearches() {

    try {

      this.showLoadingState();

      const recentSearchesData = await this.searchStrategy.loadRecentSearchResults();

      const hasAnyResults = (recentSearchesData.today && recentSearchesData.today.length > 0) ||
        (recentSearchesData.lastSevenDays && recentSearchesData.lastSevenDays.length > 0) ||
        (recentSearchesData.last30Days && recentSearchesData.last30Days.length > 0);

      if (hasAnyResults === false) {
        this.showEmptyStateNoSearches();
        return;
      }

      const groups = groupRecentSearchesByTime(recentSearchesData);
      this.displayRecentSearches(groups);

    } catch (exception) {
      console.error('Failed to load recent searches:', exception);
      this.showEmptyStateNoSearches();
    }

  }

  showLoadingState() {

    this.recentSearchesContainer.style.display = 'none';
    this.searchResultsContainer.style.display = 'none';
    this.emptyStateNoSearches.style.display = 'none';
    this.emptyStateNoResults.style.display = 'none';

  }

  showEmptyStateNoSearches() {

    this.recentSearchesContainer.style.display = 'none';
    this.searchResultsContainer.style.display = 'none';
    this.emptyStateNoSearches.style.display = 'flex';
    this.emptyStateNoResults.style.display = 'none';

  }

  showEmptyStateNoResults() {

    this.recentSearchesContainer.style.display = 'none';
    this.searchResultsContainer.style.display = 'none';
    this.emptyStateNoSearches.style.display = 'none';
    this.emptyStateNoResults.style.display = 'flex';

  }

  displaySearchResults(results) {

    this.recentSearchesContainer.style.display = 'none';
    this.emptyStateNoSearches.style.display = 'none';
    this.emptyStateNoResults.style.display = 'none';
    this.searchResultsContainer.innerHTML = '';

    if (results.length === 0) {
      this.showEmptyStateNoResults();
      return;
    }

    this.searchResultsContainer.style.display = 'flex';

    const resultsList = new SearchResultsList();
    resultsList.setOnClick(this.handleResultClick.bind(this));
    resultsList.update(results);

    mount(this.searchResultsContainer, resultsList.el);

  }

  displayRecentSearches(groups) {

    this.searchResultsContainer.style.display = 'none';
    this.emptyStateNoSearches.style.display = 'none';
    this.emptyStateNoResults.style.display = 'none';
    this.recentSearchesContainer.innerHTML = '';

    const recentSearchesDisplay = new RecentSearchesDisplay();
    recentSearchesDisplay.setOnClick(this.handleResultClick.bind(this));

    const hasResults = recentSearchesDisplay.update(groups);

    if (hasResults === false) {
      this.showEmptyStateNoSearches();
      return;
    }

    this.recentSearchesContainer.style.display = 'flex';

    mount(this.recentSearchesContainer, recentSearchesDisplay.el);

  }

  async handleResultClick(result) {

    const type = result.type;
    const data = result.data;

    if (this.onClose !== null) {
      this.onClose();
    }

    const navigationPath = this.searchStrategy.getNavigationPath(data);

    if (navigationPath !== '') {
      application.eventSystem.internal.emit(internalEventCodes.NAVIGATE_TO, {
        to: navigationPath
      });
    }

    try {
      const dataToStore = this.searchStrategy.prepareDataForStorage(data);

      await serverInterface.startup.recentSearchResults.createRecentSearchResult({
        type,
        data: dataToStore
      });
    } catch (exception) {
      console.error('Failed to store search result:', exception);
    }

  }

}

export default GenericSearchPanel;